import { Download } from "@/components/icons";
import { Badge, Button, Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type {
  DeadStockResultDTO,
  DeadStockRowDTO,
  DeadStockSuggestedAction,
} from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

const ACTION_META: Record<
  DeadStockSuggestedAction,
  {
    label: string;
    hint: string;
    variant: "destructive" | "secondary" | "outline";
  }
> = {
  markdown: {
    label: "ລົດລາຄາ",
    hint: "ຄວນລົດລາຄາ ຫຼື ປັບໂປຣ — ບໍ່ຂາຍໃນ lookback",
    variant: "destructive",
  },
  bundle: {
    label: "ແຖມໃນໂປຣ",
    hint: "ລອງແຖມ/ຊຸດ — ຍັງບໍ່ຂາຍໃນ lookback",
    variant: "secondary",
  },
  watch: {
    label: "ຈັບຕາ",
    hint: "ຂາຍຊ້າ + lot ເກົ່າ — ຕິດຕາມຕໍ່",
    variant: "outline",
  },
};

type DeadStockTableProps = {
  result?: DeadStockResultDTO;
  isLoading?: boolean;
  onExport?: () => void;
  exporting?: boolean;
  embedded?: boolean;
};

function formatDaysSince(days: number | null): string {
  if (days == null) return "ບໍ່ເຄີຍຂາຍ";
  if (days === 0) return "ມື້ນີ້";
  if (days === 1) return "1 ວັນ";
  return `${days} ວັນ`;
}

function formatOldestLotDays(days: number): string {
  if (days <= 0) return "—";
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `~${months} ເດືອນ`;
  }
  return `${days} ວັນ`;
}

function DeadStockMobileCard({ row }: { row: DeadStockRowDTO }) {
  const action = ACTION_META[row.suggestedAction];

  return (
    <ReportMobileCard>
      <ReportProductLink
        productId={row.productId}
        productName={row.productName}
        variantName={row.variantName}
      />
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat
          label="ສຕັອກ"
          value={formatQtyWithUnit(row.stockQuantity, row.stockUnitLabel)}
        />
        <ReportStat label="ຂາຍ (lookback)" value={row.quantitySoldInLookback} />
        <ReportStat label="ຄັ້ງ/ອາທິດ" value={row.ordersPerWeek} />
        <ReportStat
          label="ຂາຍລ່າສຸດ"
          value={formatDaysSince(row.daysSinceLastSale)}
        />
        <ReportStat
          label="lot ເກົ່າ"
          value={formatOldestLotDays(row.oldestLotDays)}
        />
        <ReportStat label="ມູນຄ່າຕົ້ນທຶນ" value={formatLak(row.valueAtCost)} />
      </dl>
      <div className="mt-3 border-secondary/10 border-t pt-3">
        <Badge variant={action.variant}>{action.label}</Badge>
        <p className="mt-1 text-muted-foreground text-xs leading-snug">
          {action.hint}
        </p>
      </div>
    </ReportMobileCard>
  );
}

function DeadStockDesktopRow({ row }: { row: DeadStockRowDTO }) {
  const action = ACTION_META[row.suggestedAction];

  return (
    <tr className="border-secondary/5 border-b last:border-0">
      <td className="px-4 py-2.5 sm:px-5">
        <ReportProductLink
          productId={row.productId}
          productName={row.productName}
          variantName={row.variantName}
        />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatQtyWithUnit(row.stockQuantity, row.stockUnitLabel)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {row.quantitySoldInLookback}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {row.ordersPerWeek}
      </td>
      <td className="px-4 py-2.5 sm:px-5">
        {formatDaysSince(row.daysSinceLastSale)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatOldestLotDays(row.oldestLotDays)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums sm:px-5">
        {formatLak(row.valueAtCost)}
      </td>
      <td className="px-4 py-2.5 sm:px-5">
        <Badge variant={action.variant}>{action.label}</Badge>
        <p className="mt-1 max-w-[10rem] text-muted-foreground text-xs leading-snug">
          {action.hint}
        </p>
      </td>
    </tr>
  );
}

export function DeadStockTable({
  result,
  isLoading,
  onExport,
  exporting,
  embedded = false,
}: DeadStockTableProps) {
  const rows = result?.data ?? [];
  const meta = result?.meta;

  return (
    <div className="brand-card overflow-hidden">
      {embedded ? null : (
        <div className="flex flex-col gap-3 border-secondary/15 border-b px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-5">
          <div>
            <h2 className="font-display text-lg">ສິນຄ້າຄ້າງ</h2>
            <p className="text-muted-foreground text-sm">
              ບໍ່ຂາຍໃນ lookback ຫຼື ຂາຍຊ້າ + lot ເກົ່າ — ແນະນຳ action
            </p>
            {meta ? (
              <p className="mt-1 text-muted-foreground text-xs">
                lookback {meta.lookbackDays}d · lot ≥{meta.minDays}d · ພົບ{" "}
                {meta.total} ລາຍການ
              </p>
            ) : null}
          </div>
          {onExport ? (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 rounded-full"
              disabled={exporting || isLoading}
              onClick={onExport}
            >
              <Download data-icon="inline-start" />
              {exporting ? "ກຳລັງສົ່ງອອກ..." : "CSV"}
            </Button>
          ) : null}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {["a", "b", "c", "d", "e"].map((id) => (
            <Skeleton key={id} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-muted-foreground text-sm">
          ບໍ່ມີສິນຄ້າຄ້າງຕາມເກນປັจจุบัน
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 p-4 md:hidden">
            {rows.map((row) => (
              <DeadStockMobileCard key={row.variantId} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-secondary/10 border-b text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ສຕັອກ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຂາຍ (lookback)
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ຄັ້ງ/ອາທິດ
                  </th>
                  <th className="px-4 py-2 font-medium sm:px-5">ຂາຍລ່າສຸດ</th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    lot ເກົ່າ
                  </th>
                  <th className="px-4 py-2 text-right font-medium sm:px-5">
                    ມູນຄ່າຕົ້ນທຶນ
                  </th>
                  <th className="px-4 py-2 font-medium sm:px-5">ແນະນຳ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <DeadStockDesktopRow key={row.variantId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
